import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { 
  Users, Search, ChevronRight, Mail, Phone, 
  CheckCircle, Clock, AlertCircle, ArrowLeft,
  UserPlus, Trash2, Archive, RotateCcw, UserX, CheckSquare,
  FileSpreadsheet, FileText
} from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { exportStudentsCsv, exportStudentsPdf } from '../utils/studentExport';

const StudentsManagement = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [exStudents, setExStudents] = useState([]);
  const [allClasses, setAllClasses] = useState([]); // master list from backend (register source of truth)
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [classFilter, setClassFilter] = useState('all'); // class group filter
  const [activeTab, setActiveTab] = useState('active');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, student: null, bulk: false });
  const [archiveDialog, setArchiveDialog] = useState({ open: false, student: null, bulk: false });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [processingBulk, setProcessingBulk] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  // Clear selection when switching tabs
  useEffect(() => {
    setSelectedStudents([]);
  }, [activeTab]);

  const fetchStudents = async () => {
    try {
      const res = await api.get('/students/all');
      setStudents(res.data.students || []);
      setExStudents(res.data.ex_students || []);
      setAllClasses(res.data.classes || []);
    } catch (error) {
      toast.error('Failed to load students');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = filteredStudents.map(s => s.id);
    setSelectedStudents(visibleIds);
  };

  const clearSelection = () => {
    setSelectedStudents([]);
  };

  const handleDelete = async () => {
    if (!deleteDialog.student && !deleteDialog.bulk) return;
    
    if (deleteDialog.bulk) {
      // Bulk delete
      setProcessingBulk(true);
      let successCount = 0;
      let failCount = 0;
      
      for (const studentId of selectedStudents) {
        try {
          await api.delete(`/students/${studentId}`);
          successCount++;
        } catch {
          failCount++;
        }
      }
      
      setProcessingBulk(false);
      toast.success(`Deleted ${successCount} student(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
      setSelectedStudents([]);
      setDeleteDialog({ open: false, student: null, bulk: false });
      fetchStudents();
    } else {
      // Single delete
      try {
        await api.delete(`/students/${deleteDialog.student.id}`);
        toast.success('Student deleted');
        setDeleteDialog({ open: false, student: null, bulk: false });
        fetchStudents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete student');
      }
    }
  };

  const handleArchive = async () => {
    if (!archiveDialog.student && !archiveDialog.bulk) return;
    
    if (archiveDialog.bulk) {
      // Bulk archive
      setProcessingBulk(true);
      let successCount = 0;
      let failCount = 0;
      
      for (const studentId of selectedStudents) {
        try {
          await api.post(`/students/${studentId}/archive`);
          successCount++;
        } catch {
          failCount++;
        }
      }
      
      setProcessingBulk(false);
      toast.success(`Moved ${successCount} student(s) to Ex Students${failCount > 0 ? `, ${failCount} failed` : ''}`);
      setSelectedStudents([]);
      setArchiveDialog({ open: false, student: null, bulk: false });
      fetchStudents();
    } else {
      // Single archive
      try {
        await api.post(`/students/${archiveDialog.student.id}/archive`);
        toast.success('Student moved to Ex Students');
        setArchiveDialog({ open: false, student: null, bulk: false });
        fetchStudents();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to archive student');
      }
    }
  };

  const handleBulkRestore = async () => {
    setProcessingBulk(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const studentId of selectedStudents) {
      try {
        await api.post(`/students/${studentId}/restore`);
        successCount++;
      } catch {
        failCount++;
      }
    }
    
    setProcessingBulk(false);
    toast.success(`Restored ${successCount} student(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
    setSelectedStudents([]);
    fetchStudents();
  };

  const handleRestore = async (studentId) => {
    try {
      await api.post(`/students/${studentId}/restore`);
      toast.success('Student restored');
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore student');
    }
  };

  const currentList = activeTab === 'active' ? students : exStudents;

  // Register-accurate filter: built from the master classes collection
  // (every class UPA runs), with student counts derived from each student's
  // `enrolled_class_ids` array (active bookings only — paid or pending,
  // excludes cancelled). Hard joining via class_id is exact — no more fuzzy
  // name matching, no more "0 Senior Street Dance Wednesday" when there are
  // actually students enrolled.
  const splitClassString = (raw) => String(raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const dynamicClassGroups = (() => {
    const noClassesCount = currentList.filter(
      (s) => !(s.enrolled_class_ids && s.enrolled_class_ids.length)
    ).length;

    // Count students per class_id
    const idCounts = new Map();
    currentList.forEach((s) => {
      const ids = new Set(s.enrolled_class_ids || []);
      ids.forEach((cid) => idCounts.set(cid, (idCounts.get(cid) || 0) + 1));
    });

    // Build a row per real class — every class shows up, even if 0 students.
    const groups = (allClasses || [])
      .map((c) => {
        const day = (c.day_of_week || '').trim();
        const time = (c.start_time || '').trim();
        const labelParts = [c.name];
        if (day) labelParts.push(`(${day}${time ? ` ${time}` : ''})`);
        else if (time) labelParts.push(`(${time})`);
        return {
          value: `id:${c.id}`,
          classId: c.id,
          label: labelParts.join(' '),
          name: c.name,
          day,
          count: idCounts.get(c.id) || 0,
        };
      })
      // Sort: students first (descending), ties alphabetical
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

    return [
      { value: 'all', label: 'All classes', classId: null, count: currentList.length },
      ...groups,
      { value: '__none__', label: 'No classes booked', classId: null, count: noClassesCount },
    ];
  })();

  const matchesClassFilter = (student) => {
    if (classFilter === 'all') return true;
    if (classFilter === '__none__') {
      return !(student.enrolled_class_ids && student.enrolled_class_ids.length);
    }
    if (classFilter.startsWith('id:')) {
      const cid = classFilter.slice(3);
      return (student.enrolled_class_ids || []).includes(cid);
    }
    return true;
  };

  const filteredStudents = currentList.filter(student => {
    const matchesSearch = 
      student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.parent?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.parent?.email?.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;
    if (!matchesClassFilter(student)) return false;

    if (activeTab === 'ex') return true;
    if (filterStatus === 'all') return true;
    if (filterStatus === 'paid') return student.payment_status === 'paid';
    if (filterStatus === 'pending') return student.payment_status === 'pending';
    if (filterStatus === 'not_registered') return student.payment_status === 'not_registered';
    return true;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" /> Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" /> Payment Pending</Badge>;
      case 'not_registered':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><AlertCircle className="w-3 h-3 mr-1" /> Not Registered</Badge>;
      case 'archived':
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30"><Archive className="w-3 h-3 mr-1" /> Ex Student</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">No Bookings</Badge>;
    }
  };

  const stats = {
    total: students.length,
    paid: students.filter(s => s.payment_status === 'paid').length,
    pending: students.filter(s => s.payment_status === 'pending').length,
    notRegistered: students.filter(s => s.payment_status === 'not_registered').length,
    exStudents: exStudents.length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF007F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-white/70 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                if (!filteredStudents.length) {
                  toast.error('No students to export with current filters');
                  return;
                }
                exportStudentsCsv(filteredStudents, `upa-students-${new Date().toISOString().slice(0,10)}.csv`, allClasses);
                toast.success(`Exported ${filteredStudents.length} student${filteredStudents.length === 1 ? '' : 's'} to CSV`);
              }}
              variant="outline"
              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              data-testid="export-students-csv-btn"
              title="Download the current filtered list as a CSV file (Excel)"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={() => {
                if (!filteredStudents.length) {
                  toast.error('No students to export with current filters');
                  return;
                }
                const filterBits = [];
                if (classFilter !== 'all') {
                  // Resolve the filter value to a human label rather than the raw id.
                  const group = dynamicClassGroups.find((g) => g.value === classFilter);
                  filterBits.push(`Class: ${group?.label || classFilter}`);
                }
                if (filterStatus !== 'all') filterBits.push(`Status: ${filterStatus.replace('_', ' ')}`);
                if (searchQuery) filterBits.push(`Search: "${searchQuery}"`);
                const res = exportStudentsPdf(filteredStudents, {
                  title: activeTab === 'active' ? 'UPA — Active Student Register' : 'UPA — Ex-Student Register',
                  subtitle: filterBits.join(' · '),
                  availableClasses: allClasses,
                });
                if (!res.ok) toast.error(res.error);
              }}
              variant="outline"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="export-students-pdf-btn"
              title="Open a printable register sheet (use the browser's Save as PDF option)"
            >
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button
              onClick={() => navigate('/admin/students/import')}
              className="bg-[#FF007F] hover:bg-[#D6006A]"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Students
            </Button>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
          <Users className="w-8 h-8 text-[#FF007F]" />
          Student Management
        </h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${activeTab === 'active' && filterStatus === 'all' ? 'border-[#FF007F] ring-1 ring-[#FF007F]' : 'hover:border-[#FF007F]/50'}`}
            onClick={() => { setActiveTab('active'); setFilterStatus('all'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">Active Students</p>
              <p className="text-2xl font-bold text-[#FF007F]">{stats.total}</p>
            </CardContent>
          </Card>
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${filterStatus === 'paid' ? 'border-green-500 ring-1 ring-green-500' : 'hover:border-green-500/50'}`}
            onClick={() => { setActiveTab('active'); setFilterStatus('paid'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">Paid</p>
              <p className="text-2xl font-bold text-green-400">{stats.paid}</p>
            </CardContent>
          </Card>
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${filterStatus === 'pending' ? 'border-yellow-500 ring-1 ring-yellow-500' : 'hover:border-yellow-500/50'}`}
            onClick={() => { setActiveTab('active'); setFilterStatus('pending'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">Payment Pending</p>
              <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${filterStatus === 'not_registered' ? 'border-red-500 ring-1 ring-red-500' : 'hover:border-red-500/50'}`}
            onClick={() => { setActiveTab('active'); setFilterStatus('not_registered'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">Not Registered</p>
              <p className="text-2xl font-bold text-red-400">{stats.notRegistered}</p>
            </CardContent>
          </Card>
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${activeTab === 'ex' ? 'border-gray-500 ring-1 ring-gray-500' : 'hover:border-gray-500/50'}`}
            onClick={() => { setActiveTab('ex'); setFilterStatus('all'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">Ex Students</p>
              <p className="text-2xl font-bold text-gray-400">{stats.exStudents}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search + Class Group Filter */}
        <div className="mb-6 grid gap-3 md:grid-cols-[1fr_auto]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/30 w-5 h-5" />
            <Input
              placeholder="Search by student name, parent name, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#111] border-[#333] text-white placeholder:text-white/30"
              data-testid="students-search-input"
            />
          </div>
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            className="bg-[#111] border border-[#333] text-white rounded-md px-3 py-2 min-w-[260px] focus:outline-none focus:border-[#FF007F]"
            data-testid="students-class-filter"
          >
            {dynamicClassGroups.map((g) => (
              <option key={g.value} value={g.value} className="bg-[#111]">
                {g.label}{g.value !== 'all' ? ` (${g.count})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Bulk Selection Action Bar */}
        {selectedStudents.length > 0 && (
          <div className="mb-4 p-4 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CheckSquare className="w-5 h-5 text-[#FF007F]" />
              <span className="text-white font-medium">{selectedStudents.length} student(s) selected</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearSelection}
                className="text-white/50 hover:text-white"
              >
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === 'active' ? (
                <>
                  <Button
                    onClick={() => setArchiveDialog({ open: true, student: null, bulk: true })}
                    disabled={processingBulk}
                    className="bg-gray-600 hover:bg-gray-700"
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Move to Ex Students
                  </Button>
                  <Button
                    onClick={() => setDeleteDialog({ open: true, student: null, bulk: true })}
                    disabled={processingBulk}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleBulkRestore}
                    disabled={processingBulk}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {processingBulk ? 'Restoring...' : 'Restore Selected'}
                  </Button>
                  <Button
                    onClick={() => setDeleteDialog({ open: true, student: null, bulk: true })}
                    disabled={processingBulk}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Quick Select Actions */}
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={selectAllVisible}
            className="border-[#FF007F]/50 text-[#FF007F] hover:bg-[#FF007F]/10"
          >
            <CheckSquare className="w-4 h-4 mr-2" />
            Select All ({filteredStudents.length})
          </Button>
          {selectedStudents.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="text-white/50 hover:text-white"
            >
              Clear Selection
            </Button>
          )}
        </div>

        {/* Students List */}
        <Card className="bg-[#111] border-[#333]">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span>{activeTab === 'active' ? 'Active Students' : 'Ex Students'} ({filteredStudents.length})</span>
              {filterStatus !== 'all' && activeTab === 'active' && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                  className="text-white/50 hover:text-white"
                >
                  Clear Filter
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredStudents.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">{activeTab === 'active' ? 'No students found' : 'No ex students'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredStudents.map(student => {
                  const isSelected = selectedStudents.includes(student.id);
                  return (
                  <div
                    key={student.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-all group ${
                      isSelected 
                        ? 'bg-[#FF007F]/10 border-[#FF007F]' 
                        : 'bg-[#1a1a1a] border-[#333] hover:border-[#FF007F]/50'
                    }`}
                    data-testid={`student-row-${student.id}`}
                  >
                    {/* Checkbox */}
                    <div className="pr-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleStudentSelection(student.id)}
                        className="border-white/30 data-[state=checked]:bg-[#FF007F] data-[state=checked]:border-[#FF007F]"
                      />
                    </div>
                    
                    <div 
                      className="flex items-center gap-4 flex-1 cursor-pointer"
                      onClick={() => navigate(`/admin/students/${student.id}`)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeTab === 'ex' ? 'bg-gray-500/20' : 'bg-[#FF007F]/20'}`}>
                        <span className={`font-semibold ${activeTab === 'ex' ? 'text-gray-400' : 'text-[#FF007F]'}`}>
                          {student.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold group-hover:text-[#FF007F] transition-colors">
                          {student.name || 'Unknown'}
                        </h3>
                        <div className="flex items-center gap-3 text-sm text-white/50">
                          {student.parent?.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {student.parent.email}
                            </span>
                          )}
                          {student.parent?.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {student.parent.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {student.classes?.length > 0 && (
                        <span className="text-white/50 text-sm mr-2">
                          {student.classes.length} class{student.classes.length !== 1 ? 'es' : ''}
                        </span>
                      )}
                      {activeTab === 'active' ? (
                        <>
                          {getStatusBadge(student.payment_status)}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setArchiveDialog({ open: true, student }); }}
                            className="text-gray-400 hover:text-gray-300 hover:bg-gray-500/20"
                            title="Move to Ex Students"
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, student }); }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            title="Delete Student"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleRestore(student.id); }}
                            className="text-green-400 hover:text-green-300 hover:bg-green-500/20"
                            title="Restore Student"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, student }); }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            title="Delete Permanently"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#FF007F] transition-colors" />
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, student: null, bulk: false })}>
          <DialogContent className="bg-[#111] border-[#333]">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                {deleteDialog.bulk ? 'Delete Students' : 'Delete Student'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {deleteDialog.bulk ? (
                <>
                  <p className="text-white/70">
                    Are you sure you want to permanently delete <strong className="text-white">{selectedStudents.length} student(s)</strong>?
                  </p>
                  <p className="text-red-400 text-sm mt-2">This action cannot be undone.</p>
                </>
              ) : (
                <>
                  <p className="text-white/70">
                    Are you sure you want to permanently delete <strong className="text-white">{deleteDialog.student?.name}</strong>?
                  </p>
                  <p className="text-red-400 text-sm mt-2">This action cannot be undone.</p>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, student: null, bulk: false })} className="border-[#333] text-white">
                Cancel
              </Button>
              <Button onClick={handleDelete} disabled={processingBulk} className="bg-red-500 hover:bg-red-600 text-white">
                <Trash2 className="w-4 h-4 mr-2" />
                {processingBulk ? 'Deleting...' : deleteDialog.bulk ? `Delete ${selectedStudents.length}` : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Archive Confirmation Dialog */}
        <Dialog open={archiveDialog.open} onOpenChange={(open) => setArchiveDialog({ open, student: null, bulk: false })}>
          <DialogContent className="bg-[#111] border-[#333]">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <UserX className="w-5 h-5 text-gray-400" />
                Move to Ex Students
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {archiveDialog.bulk ? (
                <>
                  <p className="text-white/70">
                    Move <strong className="text-white">{selectedStudents.length} student(s)</strong> to Ex Students?
                  </p>
                  <p className="text-white/50 text-sm mt-2">You can restore them later if needed.</p>
                </>
              ) : (
                <>
                  <p className="text-white/70">
                    Move <strong className="text-white">{archiveDialog.student?.name}</strong> to Ex Students?
                  </p>
                  <p className="text-white/50 text-sm mt-2">You can restore them later if needed.</p>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setArchiveDialog({ open: false, student: null, bulk: false })} className="border-[#333] text-white">
                Cancel
              </Button>
              <Button onClick={handleArchive} disabled={processingBulk} className="bg-gray-600 hover:bg-gray-700 text-white">
                <Archive className="w-4 h-4 mr-2" />
                {processingBulk ? 'Moving...' : archiveDialog.bulk ? `Move ${selectedStudents.length}` : 'Move to Ex Students'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default StudentsManagement;
